import React from "react";
import { ArrowRight, Star, TrendingUp, Shield, Clock } from "lucide-react";

interface FirmRecommendationsProps {
  result: any;
}

interface Firm {
  name: string;
  link: string;
  maxLeverage: string;
  maxDrawdown: string;
  challengeType: string;
  rating?: number;
  reviewCount?: number;
  bestFeature?: string;
  affiliateLink?: string;
}

const firmData: Record<string, Firm[]> = {
  instant: [
    {
      name: "FXIFY",
      link: "/reviews/fxify",
      maxLeverage: "1:100",
      maxDrawdown: "10%",
      challengeType: "Instant / 2-Step",
      rating: 4.8,
      reviewCount: 1200,
      bestFeature: "Fastest Payouts",
      affiliateLink: "/go/fxify"
    },
    {
      name: "City Traders Imperium",
      link: "/reviews/cti",
      maxLeverage: "1:30",
      maxDrawdown: "5% daily, 10% max",
      challengeType: "Instant / 2-Step",
      rating: 4.7,
      reviewCount: 850,
      bestFeature: "Lowest Daily Drawdown",
      affiliateLink: "/go/cti"
    }
  ],
  "1-step": [
    {
      name: "E8 Funding",
      link: "/reviews/e8",
      maxLeverage: "1:100",
      maxDrawdown: "8%",
      challengeType: "1-Step",
      rating: 4.9,
      reviewCount: 1500,
      bestFeature: "Highest Success Rate",
      affiliateLink: "/go/e8"
    },
    {
      name: "FXIFY",
      link: "/reviews/fxify",
      maxLeverage: "1:100",
      maxDrawdown: "10%",
      challengeType: "Instant / 2-Step",
      rating: 4.8,
      reviewCount: 1200,
      bestFeature: "Fastest Payouts",
      affiliateLink: "/go/fxify"
    }
  ],
  "2-step": [
    {
      name: "Alpha Capital Group",
      link: "/reviews/alpha-capital",
      maxLeverage: "1:30",
      maxDrawdown: "10%",
      challengeType: "2-Step",
      rating: 4.6,
      reviewCount: 950,
      bestFeature: "Best Support",
      affiliateLink: "/go/alpha"
    },
    {
      name: "E8 Funding",
      link: "/reviews/e8",
      maxLeverage: "1:100",
      maxDrawdown: "8%",
      challengeType: "1-Step",
      rating: 4.9,
      reviewCount: 1500,
      bestFeature: "Highest Success Rate",
      affiliateLink: "/go/e8"
    },
    {
      name: "WeMasterTrade",
      link: "/reviews/wmt",
      maxLeverage: "1:100",
      maxDrawdown: "8%",
      challengeType: "2-Step",
      rating: 4.7,
      reviewCount: 1100,
      bestFeature: "Best Education",
      affiliateLink: "/go/wmt"
    }
  ],
  "3-step": [
    {
      name: "City Traders Imperium",
      link: "/reviews/cti",
      maxLeverage: "1:30",
      maxDrawdown: "5% daily, 10% max",
      challengeType: "Instant / 2-Step",
      rating: 4.7,
      reviewCount: 850,
      bestFeature: "Lowest Daily Drawdown",
      affiliateLink: "/go/cti"
    }
  ]
};

export const FirmRecommendations: React.FC<FirmRecommendationsProps> = ({ result }) => {
  if (!result) return null;

  const matchingFirms = firmData[result.challengeType] || [];

  if (matchingFirms.length === 0) return null;

  return (
    <div className="bg-[#2a2a2a] rounded-2xl shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-neutral-100">Recommended Firms for Your Strategy</h2>
        <a 
          href="/best-prop-firms" 
          className="text-yellow-400 hover:text-yellow-300 text-sm flex items-center gap-1"
        >
          View All Firms <ArrowRight className="w-4 h-4" />
        </a>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {matchingFirms.map((firm, idx) => (
          <div key={idx} className="bg-[#232323] rounded-xl p-4 hover:bg-[#2a2a2a] transition-colors">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-yellow-400">{firm.name}</h3>
              {firm.rating && (
                <div className="flex items-center gap-1 text-yellow-400">
                  <Star className="w-4 h-4 fill-current" />
                  <span className="text-sm">{firm.rating}</span>
                  <span className="text-xs text-neutral-400">({firm.reviewCount})</span>
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <TrendingUp className="w-4 h-4 text-yellow-400" />
                <span>Max Leverage: {firm.maxLeverage}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <Shield className="w-4 h-4 text-yellow-400" />
                <span>Max Drawdown: {firm.maxDrawdown}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <Clock className="w-4 h-4 text-yellow-400" />
                <span>{firm.challengeType}</span>
              </div>
            </div>

            {firm.bestFeature && (
              <div className="mb-4">
                <span className="inline-block bg-yellow-400/10 text-yellow-400 text-xs px-2 py-1 rounded-full">
                  {firm.bestFeature}
                </span>
              </div>
            )}

            <div className="flex gap-2">
              <a
                href={firm.affiliateLink}
                className="flex-1 bg-yellow-400 text-black font-semibold px-4 py-2 rounded-lg text-center hover:bg-yellow-300 transition"
              >
                Get Started
              </a>
              <a
                href={firm.link}
                className="flex-1 bg-white/10 text-neutral-200 font-semibold px-4 py-2 rounded-lg text-center hover:bg-white/20 transition"
              >
                Read Review
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 text-center">
        <p className="text-sm text-neutral-400">
          Based on your calculated lot sizes and risk parameters, these firms offer the best conditions for your trading strategy.
        </p>
      </div>
    </div>
  );
};

export default FirmRecommendations; 